import React from "react";
import { useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import moment from "moment";
import { Item } from "features/preauthorization/components/NestedPayerResponse";

const ClaimProviderOptical = () => {
  const { viewClaimInfo } = useSelector((state) => state.claim);

  const visionPreauthorization = viewClaimInfo?.payload_json?.prescp || {};

  // Filter to only get prescription objects (those with "eye" property)
  const visionDetails = Object.values(visionPreauthorization).filter(
    (item) => item && typeof item === "object" && item.eye
  );

  // Get metadata from prescp object directly
  const productType = visionPreauthorization?.lens_typ;

  // Modified function to handle cases without notes
  const getLensSpec = (eye, type) => {
    const eyeLower = eye.toLowerCase();
    const typeLower = type.toLowerCase();

    // First try to find by note (note is an array in the data)
    const byNote = visionDetails.find((l) => {
      if (l.eye?.toLowerCase() !== eyeLower) return false;

      const notes = l.note || l.notes || [];
      return notes.some((n) => {
        const noteText = (n.text || n || "").toLowerCase();
        return noteText === typeLower;
      });
    });

    if (byNote) return byNote;

    // Get all entries for this eye
    const eyeEntries = visionDetails.filter(
      (l) => l.eye?.toLowerCase() === eyeLower
    );

    if (eyeEntries.length === 0) return null;

    // If only one entry for this eye, return it (works for both lens and contact)
    if (eyeEntries.length === 1) return eyeEntries[0];

    // For lens type with multiple entries, determine by sphere value
    if (productType === "lens" && eyeEntries.length > 1) {
      if (typeLower === "distance") {
        // Find entry with positive or zero sphere, or first one
        const distanceEntry = eyeEntries.find((e) => {
          const sphere = parseFloat(e.sphere) || 0;
          return sphere >= 0;
        });
        return distanceEntry || eyeEntries[0];
      }
      if (typeLower === "near") {
        // Find entry with negative sphere, or second one
        const nearEntry = eyeEntries.find((e) => {
          const sphere = parseFloat(e.sphere) || 0;
          return sphere < 0;
        });
        return nearEntry || eyeEntries[1] || eyeEntries[0];
      }
    }

    // For contact type with multiple entries, try to find by index order
    // Typically: first entry is distance, second is near
    if (productType === "contact" && eyeEntries.length > 1) {
      if (typeLower === "distance") {
        return eyeEntries[0];
      }
      if (typeLower === "near") {
        return eyeEntries[1] || eyeEntries[0];
      }
    }

    // Fallback: return first entry
    return eyeEntries[0];
  };

  const getPrism = (lens, base) =>
    lens?.prism?.find((p) => p.base?.toLowerCase() === base?.toLowerCase())
      ?.amount || "";

  const fields =
    productType === "contact"
      ? [
          { label: "POWER", name: "power" },
          { label: "CYLINDER", name: "cylinder" },
          { label: "AXIS", name: "axis" },
          { label: "BACKCURVE", name: "bckcurve" },
          { label: "DIAMETER", name: "dmeter" },
          { label: "ADD", name: "add" },
          { label: "COLOR", name: "color" },
          { label: "BRAND", name: "brand" },

          { label: "PD", name: "pd" },
        ]
      : [
          { label: "SPHERE", name: "sphere" },
          { label: "CYLINDER", name: "cylinder" },
          { label: "AXIS", name: "axis" },
          { label: "IN", name: "in" },
          { label: "OUT", name: "out" },
          { label: "DOWN", name: "down" },
          { label: "UP", name: "up" },
          { label: "ADD", name: "add" },
          { label: "COLOR", name: "color" },
          { label: "BRAND", name: "brand" },
          { label: "PD", name: "pd" },
        ];

  const leftDistance = getLensSpec("left", "distance");
  const leftNear = getLensSpec("left", "near");
  const rightDistance = getLensSpec("right", "distance");
  const rightNear = getLensSpec("right", "near");

  const opticalData = [
    {
      id: 0,
      title: "Optical Type",
      value: productType || "-",
    },
    {
      id: 1,
      title: "Optical Date",
      value: visionPreauthorization?.prescp_dte
        ? moment(visionPreauthorization.prescp_dte).format("DD-MM-YYYY")
        : "-",
    },
    {
      id: 2,
      title: "Is Frame",
      value:
        visionPreauthorization?.isframe !== undefined
          ? visionPreauthorization.isframe
            ? "Yes"
            : "No"
          : "-",
    },
    {
      id: 3,
      title: "Regular Contact",
      value: visionPreauthorization?.regular_contact || "-",
    },
    {
      id: 4,
      title: "Regular Lenses",
      value: visionPreauthorization?.regular_lenses || "-",
    },
  ];

  return (
    <div>
      {/* Info Header */}
      <Row>
        {opticalData.map((item) => (
          <Col key={item?.id} xs={12} md={4} xl={3} className="mb-3">
            <Item title={item.title} value={item.value} remark={false} />
          </Col>
        ))}
        <Col xs={12} md={4} xl={3} className="mb-3">
          <div className="d-flex flex-column gap-1 w-100">
            <div className="fs-10 fw-bold">Lens Specifications</div>
            <div className="text-600 fw-normal text-capitalize fs-10">
              {Array.isArray(visionPreauthorization?.lens_specifications) &&
              visionPreauthorization?.lens_specifications?.length > 0
                ? visionPreauthorization.lens_specifications.join(" - ")
                : viewClaimInfo?.other_fields?.vision_extraflds
                    ?.lens_specifications &&
                  Array.isArray(
                    viewClaimInfo?.other_fields?.vision_extraflds
                      ?.lens_specifications
                  ) &&
                  viewClaimInfo?.other_fields?.vision_extraflds
                    ?.lens_specifications?.length > 0
                ? viewClaimInfo?.other_fields?.vision_extraflds?.lens_specifications.join(
                    " - "
                  )
                : "-"}
            </div>
          </div>
        </Col>
      </Row>

      <div className="table-responsive mt-3">
        <table
          className="table align-middle text-center"
          style={{ border: "1px solid #dee2e6", tableLayout: "fixed" }}
        >
          <thead>
            <tr>
              <th
                colSpan={2}
                className="text-success border border-secondary"
                style={{ width: "40%" }}
              >
                Left
              </th>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              ></th>
              <th
                colSpan={2}
                className="text-success border border-secondary"
                style={{ width: "40%" }}
              >
                Right
              </th>
            </tr>
            <tr>
              <th className="border border-secondary" style={{ width: "20%" }}>
                Distance
              </th>
              <th className="border border-secondary" style={{ width: "20%" }}>
                Near
              </th>
              <th
                className="border border-secondary"
                style={{ width: "20%" }}
              ></th>
              <th className="border border-secondary" style={{ width: "20%" }}>
                Distance
              </th>
              <th className="border border-secondary" style={{ width: "20%" }}>
                Near
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => {
              // Helper function to get field value from entry
              const getFieldValue = (entry, fieldName) => {
                if (!entry) return "";

                // Handle prism fields
                if (
                  fieldName === "in" ||
                  fieldName === "out" ||
                  fieldName === "up" ||
                  fieldName === "down"
                ) {
                  return getPrism(entry, fieldName);
                }

                // Handle regular fields
                return entry[fieldName] || "";
              };

              return (
                <tr key={field.name}>
                  {/* LEFT DISTANCE */}
                  <td
                    className="border border-secondary"
                    style={{ width: "20%" }}
                  >
                    {getFieldValue(leftDistance, field.name)}
                  </td>
                  {/* LEFT NEAR */}
                  <td
                    className="border border-secondary"
                    style={{ width: "20%" }}
                  >
                    {getFieldValue(leftNear, field.name)}
                  </td>
                  {/* LABEL */}
                  <td
                    className="text-uppercase text-center text-nowrap border border-secondary"
                    style={{ width: "20%" }}
                  >
                    <strong className="fs-10">{field.label}</strong>
                  </td>
                  {/* RIGHT DISTANCE */}
                  <td
                    className="border border-secondary"
                    style={{ width: "20%" }}
                  >
                    {getFieldValue(rightDistance, field.name)}
                  </td>
                  {/* RIGHT NEAR */}
                  <td
                    className="border border-secondary"
                    style={{ width: "20%" }}
                  >
                    {getFieldValue(rightNear, field.name)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClaimProviderOptical;
